import { Box, Button, Card, Flex, Skeleton } from '@radix-ui/themes';
import { BreakCanvas } from 'classes/break-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-header';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { isAppearanceDark } from 'index';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IGroupedFires } from 'lib_ts/interfaces/i-pitch-performance';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

const COMPONENT_NAME = 'PitchPerfPlotBreaks';

const REDRAW_DEQUEUE_MS = 100;
const ENABLE_STATCAST_BREAK_CIRCLES = false;

export const PitchPerfPlotBreaks = (props: { groups: IGroupedFires[] }) => {
  const {
    loading,
    selectedShotIds,
    filters,
    isAveraged,
    showTargets,
    showThresholds,
    statsPitchGroups,
    statsPitcherPk,
    setStatsDialog,
  } = usePitchPerformanceStore(
    useShallow((state) => ({
      loading: state.loading,
      selectedShotIds: state.selectedShotIds,
      filters: state.filters,

      isAveraged: state.isAveraged,
      showThresholds: state.showThresholds,
      showTargets: state.showTargets,

      statsPitchGroups: state.statsPitchGroups,

      statsPitcherPk: state.statsPitcherPk,
      setStatsDialog: state.setStatsDialog,
    }))
  );

  const [canvasDef] = useState(BreakCanvas.makeBigSquare(isAppearanceDark()));

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const rulersCanvas = useRef<HTMLCanvasElement>(null);
  const gridCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        gridCanvas.current,
        rulersCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawRulers and drawGrid
  useEffect(() => {
    async function drawRulers() {
      try {
        console.debug(`${COMPONENT_NAME}: draw rulers`);

        const canvas = rulersCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvasDef.drawRulersForPitchPerformance(ctx, 10);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    }

    async function drawGrid() {
      try {
        console.debug(`${COMPONENT_NAME}: draw grid`);

        const canvas = gridCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvasDef.drawGrid(ctx, 10, true);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    }

    drawGrid();
    drawRulers();
  }, [loading]);

  // drawBreaks
  useEffect(() => {
    try {
      console.debug(`${COMPONENT_NAME}: draw break data points`);

      const canvas = shotsCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const visibleStatsGroups = statsPitchGroups
        .filter((g) => !g.invisible)
        .filter(
          (g) => props.groups.findIndex((gr) => gr.type === g.type) !== -1
        );

      for (const statsGroup of visibleStatsGroups) {
        // draw each break as an X
        for (const b of statsGroup.breaks) {
          canvasDef.drawCross2(ctx, {
            value: {
              xInches: -b.xInches,
              zInches: b.zInches,
            },
            color: `rgba(${statsGroup.rgb},100%)`,
            width_px: 10,
          });
        }

        // draw a circle centered on the avg break with radius to encompass every individual break
        if (
          ENABLE_STATCAST_BREAK_CIRCLES &&
          statsGroup.avgBreak &&
          statsGroup.maxBreakDistanceIn > 0
        ) {
          canvasDef.drawBreakCircle(ctx, {
            value: {
              xInches: -statsGroup.avgBreak.xInches,
              zInches: statsGroup.avgBreak.zInches,
            },
            // add a small amount so the circle encompasses the furthest break
            radiusIn: statsGroup.maxBreakDistanceIn + 0.6,
            borderColor: `rgba(${statsGroup.rgb}, 75%)`,
            borderWidth: 2,
          });
        }
      }

      for (const group of props.groups) {
        const targetValue: ITrajektRefBreak = {
          xInches: -1 * group.target.break.xInches,
          zInches: group.target.break.zInches,
        };

        if (showThresholds) {
          canvasDef.drawBreak(ctx, {
            value: targetValue,
            radiusIn: MAX_BREAKS_DELTA_INCHES,
            fillColor: `rgba(${group.rgb},25%)`,
            borderColor: `rgba(${group.rgb},75%)`,
            borderWidth: 2,
          });
        }

        if (showTargets) {
          canvasDef.drawCrosshairs(ctx, {
            value: targetValue,
            color: `rgba(${group.rgb},100%)`,
            width_px: 20,
          });
        }

        const avgValue: ITrajektRefBreak = {
          xInches: -1 * group.average.break.xInches,
          zInches: group.average.break.zInches,
        };

        if (isAveraged && showTargets) {
          canvasDef.drawLine(ctx, {
            from: avgValue,
            to: targetValue,
            lineColor: `rgba(${group.rgb},100%)`,
          });

          canvasDef.drawLabel(ctx, {
            value: avgValue,
            target: targetValue,
            fontColor: `rgba(${group.rgb},100%)`,
          });
        }

        if (isAveraged) {
          canvasDef.drawBreak(ctx, {
            value: avgValue,
            radiusIn: 0.5,
            fillColor: `rgba(${group.rgb},75%)`,
            borderColor: `rgba(${group.rgb},100%)`,
            borderWidth: 1,
          });
          continue;
        }

        // if the user has selected specific shots, only draw those
        const shots =
          selectedShotIds.length > 0
            ? group.rotatedShots.filter(({ _id }) =>
                selectedShotIds.includes(_id)
              )
            : group.rotatedShots;

        for (const shot of shots) {
          if (!shot.traj) {
            continue;
          }

          const actual = TrajHelper.getBreaksFromShot(shot);

          const value: ITrajektRefBreak = {
            xInches: -1 * actual.xInches,
            zInches: actual.zInches,
          };

          if (showTargets && isAveraged) {
            canvasDef.drawLine(ctx, {
              from: value,
              to: targetValue,
              lineColor: `rgba(${group.rgb},100%)`,
            });

            canvasDef.drawLabel(ctx, {
              value: value,
              target: targetValue,
              fontColor: `rgba(${group.rgb},100%)`,
            });
          }

          canvasDef.drawBreak(ctx, {
            value: value,
            radiusIn: 0.5,
            fillColor: `rgba(${group.rgb},75%)`,
            borderColor: `rgba(${group.rgb},100%)`,
            borderWidth: 1,
          });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [
    props.groups,
    statsPitchGroups,
    isAveraged,
    showTargets,
    showThresholds,
    selectedShotIds,
  ]);

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3">
          <PlotHeader
            title="Break"
            subtitle="Scatter plot of breaks (in) with Hz and Vt components"
            children={
              <Button
                size="1"
                variant="soft"
                color={
                  statsPitcherPk ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL
                }
                onClick={() => setStatsDialog(Date.now())}
              >
                <Flex gap="1" align="center">
                  <img width="16" src="/img/statcast-logo.svg" />
                  <Box>Statcast Overlay</Box>
                </Flex>
              </Button>
            }
          />

          <Card
            style={{
              padding: 0,
            }}
          >
            <div className="slider">
              <div
                className="sizer"
                style={{
                  aspectRatio: '1.00',
                }}
              >
                {/* for log rocket recordings to show the results */}
                {imgSrc && (
                  <img
                    alt="break performance plot"
                    className="image-wrapper"
                    src={imgSrc}
                  />
                )}
                <canvas
                  ref={mergeCanvas}
                  data-identifier="merge-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={rulersCanvas}
                  data-identifier="rulers-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={gridCanvas}
                  data-identifier="grid-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
                <canvas
                  ref={shotsCanvas}
                  data-identifier="shots-canvas"
                  width={canvasDef.CONFIG.canvas.width_px}
                  height={canvasDef.CONFIG.canvas.height_px}
                />
              </div>
            </div>
          </Card>
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
